import {
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayConfig,
  OverlayRef,
  ScrollDispatcher
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  booleanAttribute,
  Directive,
  effect,
  ElementRef,
  HostListener,
  inject,
  Injector,
  input,
  InputSignalWithTransform,
  OnDestroy,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { BooleanInputType } from '../core';

import { TooltipComponent } from './tooltip.component';
import { TOOLTIP_CONNECTION_POSITION_PAIR, TooltipConnectionPositionPair } from './tooltip-connection-position-pair';
import { TooltipPosition } from './tooltip-position';

@Directive({
  selector: '[tiimeTooltip]',
  exportAs: 'tiime-tooltip',
  standalone: false
})
export class TooltipDirective implements OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly overlay = inject(Overlay);
  private readonly scrollDispatcher = inject(ScrollDispatcher);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly injector = inject(Injector);

  readonly content = input<TemplateRef<any>>(undefined!);
  readonly tooltipShowDelay = input(0);
  readonly tooltipHideDelay = input(0);
  readonly hideWithoutEllipsis: InputSignalWithTransform<boolean, BooleanInputType> = input<boolean, BooleanInputType>(
    false,
    { transform: booleanAttribute }
  );

  readonly tooltipPosition = input<TooltipPosition>('right');
  readonly tooltipInvertedPosition = input<TooltipPosition | null>(null);
  readonly tooltipDisabled = input(false);
  readonly tooltipClass = input<string | string[] | Set<string> | Record<string, any> | null>(null);

  tooltipInstance: TooltipComponent | null;

  private overlayRef: OverlayRef;
  private tooltipPortal: ComponentPortal<TooltipComponent>;

  /** Emits when the component is destroyed. */
  private readonly destroyed = new Subject<void>();

  constructor() {
    effect(() => {
      if (this.tooltipInstance) {
        this.setTooltipClass(this.tooltipClass());
      }
    });
    effect(() => {
      if (this.tooltipInstance && this.tooltipDisabled()) {
        this.hide(0);
      }
    });
    effect(() => {
      // On fait une lecture ici pour déclencher l'effect
      this.tooltipPosition();
      if (this.overlayRef) {
        const positionStrategy = this.overlayRef.getConfig().positionStrategy as FlexibleConnectedPositionStrategy;
        this.setPosition(positionStrategy);
        this.overlayRef.updatePosition();
      }
    });
  }

  private get hasEllipsis(): boolean {
    return (
      this.elementRef.nativeElement.offsetWidth < this.elementRef.nativeElement.scrollWidth ||
      this.elementRef.nativeElement.offsetHeight < this.elementRef.nativeElement.scrollHeight
    );
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if ((!this.hasEllipsis && this.hideWithoutEllipsis()) || this.tooltipDisabled()) {
      return;
    }
    this.show();
  }
  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hide();
  }

  /**
   * Dispose the tooltip when destroyed.
   */
  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
    this.destroyed.next();
    this.destroyed.complete();
  }

  show(delay: number = this.tooltipShowDelay()): void {
    const overlayRef = this.createOverlay();
    this.detach();
    // Create ComponentPortal that can be attached to a PortalHost
    this.tooltipPortal = this.tooltipPortal || new ComponentPortal(TooltipComponent, this.viewContainerRef);

    // Attach ComponentPortal to PortalHost
    this.tooltipInstance = overlayRef.attach(this.tooltipPortal).instance;
    this.tooltipInstance
      .afterHidden()
      .pipe(
        takeUntil(this.destroyed),
        tap(() => this.detach())
      )
      .subscribe();
    this.updateTooltipContent();
    const positionStrategy = this.overlayRef.getConfig().positionStrategy as FlexibleConnectedPositionStrategy;
    this.setPosition(positionStrategy);
    this.setTooltipClass(this.tooltipClass());

    this.tooltipInstance.show(delay);
  }

  hide(delay: number = this.tooltipHideDelay()): void {
    if (this.tooltipInstance) {
      this.tooltipInstance.hide(delay);
    }
  }

  private createOverlay(): OverlayRef {
    if (this.overlayRef) {
      return this.overlayRef;
    }

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withTransformOriginOn('.tooltip-container')
      .withFlexibleDimensions(false)
      .withViewportMargin(10);

    this.setPosition(positionStrategy);

    const scrollableAncestors = this.scrollDispatcher.getAncestorScrollContainers(this.elementRef);

    positionStrategy.withScrollableContainers(scrollableAncestors);

    const overlayConfig = new OverlayConfig({
      hasBackdrop: false,
      panelClass: 'tooltip-overlay',
      scrollStrategy: this.overlay.scrollStrategies.close(),
      positionStrategy
    });

    this.overlayRef = this.overlay.create(overlayConfig);

    this.overlayRef
      .detachments()
      .pipe(
        takeUntil(this.destroyed),
        tap(() => this.detach())
      )
      .subscribe();
    return this.overlayRef;
  }

  private updateTooltipContent(): void {
    if (this.tooltipInstance) {
      this.tooltipInstance.content = this.content();
      this.tooltipInstance.markForCheck();
    }
  }

  /** Detaches the currently-attached tooltip. */
  private detach(): void {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }
    if (this.tooltipInstance) {
      this.tooltipInstance = null;
    }
  }

  /** Set the position of the current tooltip. */
  private setPosition(positionStrategy: FlexibleConnectedPositionStrategy): void {
    const mainPosition = this.getTooltipConnectionPositionPair(this.tooltipPosition());
    let invertedPosition: TooltipPosition;
    if (this.tooltipInvertedPosition()) {
      invertedPosition = this.tooltipInvertedPosition()!;
    } else {
      switch (this.tooltipPosition()) {
        case 'right':
          invertedPosition = 'left';
          break;
        case 'left':
          invertedPosition = 'right';
          break;
        case 'bottom':
          invertedPosition = 'top';
          break;
        case 'top':
          invertedPosition = 'bottom';
          break;
        case 'above':
          invertedPosition = 'below';
          break;
        case 'below':
          invertedPosition = 'above';
          break;
        case 'below-left':
          invertedPosition = 'above-left';
          break;
        case 'above-left':
          invertedPosition = 'below-left';
          break;
        case 'below-right':
          invertedPosition = 'above-right';
          break;
        case 'above-right':
          invertedPosition = 'below-right';
          break;
        case 'above-start':
          invertedPosition = 'below-start';
          break;
        case 'below-start':
          invertedPosition = 'above-start';
          break;
      }
    }
    const fallbackPosition = this.getTooltipConnectionPositionPair(invertedPosition);
    positionStrategy.withPositions([mainPosition, fallbackPosition]);
  }

  private getTooltipConnectionPositionPair(position: TooltipPosition): TooltipConnectionPositionPair {
    const tooltipConnectionPositionPair = TOOLTIP_CONNECTION_POSITION_PAIR[position];
    if (!tooltipConnectionPositionPair) {
      throw new Error(`Tooltip position "${position}" is invalid.`);
    }
    return tooltipConnectionPositionPair;
  }

  private setTooltipClass(tooltipClass: string | string[] | Set<string> | Record<string, any> | null): void {
    if (this.tooltipInstance) {
      this.tooltipInstance.tooltipClass = tooltipClass;
      this.tooltipInstance.markForCheck();
    }
  }
}
